import InfoTooltip from '@components/InfoTooltip'
import AutoCompleteChip from '@components/standardAutoCompleteSearch/AutoCompleteChip'
import StandardAutoCompleteSearch, {
  AutoCompleteOption,
} from '@components/standardAutoCompleteSearch/StandardAutoCompleteSearch'
import { colors } from '@globals/themes/defaultTheme'
import { semiBoldFontWeight } from '@globals/themes/themeConstants'
import CheckIcon from '@mui/icons-material/Check'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { Selectable } from '@shared/filters/selections'
import { TFunction } from '@shared/types/types'
import { T, useT } from '@transifex/react'
import { useEffect, useMemo, useState } from 'react'
import {
  Control,
  Controller,
  FieldName,
  FieldPathValue,
  FieldValues,
  Path,
  useFormContext,
  useWatch,
} from 'react-hook-form'
import z from 'zod'
import ErrorText from './ErrorText'
import FieldLabel from './FieldLabel'

type QualityAutoCompleteValue = {
  name: string
  group?: string
  color?: string
}
type QualitiesAutoCompleteFieldProps<T extends FieldValues> = {
  name: Path<T>
  control: Control<T>
  options: Selectable[]
  isEditing?: boolean
}

export const createQualitiesSchema = (t: TFunction, qualitiesRequired: boolean) =>
  z.object({
    qualities: qualitiesRequired
      ? z.array(z.custom<Selectable>()).min(1, { message: t('Qualities are required') })
      : z.array(z.custom<Selectable>()),
  })

const QualitiesAutoCompleteField = <T extends FieldValues>({
  name,
  control,
  options,
  isEditing = false,
}: QualitiesAutoCompleteFieldProps<T>) => {
  const t = useT()
  const [searchText, setSearchText] = useState<string>('')
  const { setValue } = useFormContext()
  const selectedQualities = useWatch({ control, name })
  const selectedQualityIds: string[] = useMemo(
    () => selectedQualities.map((quality: Selectable) => quality.id),
    [selectedQualities],
  )
  const [filteredOptions, setFilteredOptions] = useState<
    AutoCompleteOption<QualityAutoCompleteValue>[]
  >([])

  useEffect(() => {
    const filtered = options
      .filter(option => option.name.toLowerCase().includes(searchText.toLowerCase()))
      .map(option => ({
        id: option.id,
        value: { name: option.name, group: option.tooltipValue, color: option.color },
      }))
    setFilteredOptions(filtered)
  }, [searchText, options, selectedQualityIds])

  const onSelectedOptionsChanged = (
    selectedOptions: AutoCompleteOption<QualityAutoCompleteValue>[],
  ) => {
    let options = selectedOptions.map(option => ({
      id: option.id,
      ...option.value,
    }))

    const lastOption = options[options.length - 1]
    if (
      lastOption &&
      options.length > selectedQualityIds.length &&
      selectedQualityIds.includes(lastOption.id)
    ) {
      options = options.filter(o => o.id !== lastOption.id)
    }

    setValue(name, options as FieldPathValue<FieldValues, FieldName<FieldValues>>, {
      shouldValidate: true,
    })
  }

  if (options.length === 0) return null

  return (
    <Stack useFlexGap gap={1} mt={3}>
      <Stack direction="row" alignItems="center" useFlexGap gap={1}>
        <FieldLabel labelStyle={{ m: 0 }}>
          <T _str={isEditing ? 'Edit qualities' : 'Select qualities'} />
        </FieldLabel>
        <Box height={25}>
          <InfoTooltip
            title={
              <T _str="Tagging a quality makes your message more meaningful by showing how someone’s actions reflect organizational values" />
            }
            color={colors.bluePrimary}
          />
        </Box>
      </Stack>
      <Controller
        name={name}
        control={control}
        render={({ fieldState: { error } }) => (
          <>
            <StandardAutoCompleteSearch<QualityAutoCompleteValue>
              searchText={searchText}
              value={selectedQualities.map((quality: Selectable) => ({
                id: quality.id,
                value: { name: quality.name, group: quality.tooltipValue, color: quality.color },
              }))}
              disableClose
              options={filteredOptions}
              onSearchTextChanged={setSearchText}
              onSelectedOptionsChanged={onSelectedOptionsChanged}
              placeholder={t('Search for a quality...')}
              renderChip={(option, props) => {
                const { key, ...otherProps } = props
                return (
                  <AutoCompleteChip
                    key={key}
                    {...otherProps}
                    label={option.value.name}
                    backgroundColor={colors.white}
                    borderColor={option.value.color}
                  />
                )
              }}
              renderOption={(props, option) => {
                const isSelected = selectedQualityIds.includes(option.id)
                return (
                  <Box {...props} component="li" key={option.id}>
                    <Stack direction="row" useFlexGap justifyContent="space-between" width="100%">
                      <Typography
                        variant="caption"
                        fontWeight={isSelected ? semiBoldFontWeight : 'normal'}>
                        {option.value.name}
                      </Typography>
                      {isSelected && <CheckIcon />}
                    </Stack>
                  </Box>
                )
              }}
              groupBy={option => (option.value.group ? `${t('Value')}: ${option.value.group}` : '')}
              zIndexPlacement="modal"
              debounceInput={false}
            />
            {!!error && <ErrorText>{error.message}</ErrorText>}
          </>
        )}
      />
    </Stack>
  )
}

export default QualitiesAutoCompleteField
